import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { ExchangeRateDto } from '@kodit/core/data-api';
import { SharedService } from '@kodit/core/services';
import { round, SERBIA_CURRENCY_ALPHA_CHAR } from '@kodit/core/shared';

export interface KonverzijaUValutuDokumenta {
  iznos: number;
  /** Srednji kurs po kom je iznos dobijen, null ako je iznos unet rucno */
  kurs: number | null;
}

type NacinKonverzije = 'srednji' | 'rucno';

@Component({
  selector: 'kodit-rezultat-pretrage-kurs',
  templateUrl: './rezultat-pretrage-kurs.component.html',
  styleUrls: ['./rezultat-pretrage-kurs.component.scss'],
})
export class RezultatPretrageKursComponent implements OnInit, OnChanges {
  iznosPoSrednjemKursu: number = 0;
  iznosPoProdajnomKursu: number = 0;
  odabraniKurs: number;
  middleRate: number;
  sellingRate: number;

  /** Konverzija RSD artikla u valutu dokumenta */
  nacinKonverzije: NacinKonverzije = 'srednji';
  iznosUValutiDokumenta: number = 0;
  rucniIznos: number = null;
  /** Sami izaberemo rucni unos (a nije prinudno, zbog nedostupnog kursa) */
  private _rucnoOdabrano: boolean = false;

  @Input() valutaText: string = SERBIA_CURRENCY_ALPHA_CHAR;
  @Input() iznosUStranojValuti: number = 0;
  @Input() rowId: number = 0;
  @Output() iznosChanged = new EventEmitter<number>();

  /**
   * Kada je true, iznos iz cenovnika je u RSD i preracunava se u valutu dokumenta
   * (deljenjem sa srednjim kursom dokumenta), umesto iz strane valute u RSD.
   */
  @Input() uValutuDokumenta: boolean = false;
  @Input() valutaDokumenta: string;
  @Input() kursDokumenta: number;
  @Input() iznosURsd: number = 0;
  @Output() konverzijaChanged = new EventEmitter<KonverzijaUValutuDokumenta>();

  constructor(private _service: SharedService) {}

  get jeKursDokumentaDostupan(): boolean {
    return this.kursDokumenta > 0;
  }

  ngOnInit(): void {
    if (this.uValutuDokumenta) {
      return;
    }
    this._load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.uValutuDokumenta) {
      if (changes['kursDokumenta'] || changes['iznosURsd']) {
        this._calculationUValutuDokumenta();
      }
      return;
    }
    if (changes['iznosUStranojValuti']?.currentValue) {
      this._calculation();
    }
  }

  private _load() {
    this._service.getKursEvra.subscribe((res: ExchangeRateDto) => {
      if (!res) {
        return;
      }
      this.middleRate = res.middleRate;
      this.sellingRate = res.sellingRate;
      this._calculation();
    });
  }

  private _calculation(){
    this.iznosPoSrednjemKursu = this.middleRate * this.iznosUStranojValuti;
    this.iznosPoProdajnomKursu = this.sellingRate * this.iznosUStranojValuti;
    this.odabraniKurs = this.iznosPoSrednjemKursu;
    this.iznosChanged.emit(this.iznosPoSrednjemKursu);
  }

  private _calculationUValutuDokumenta() {
    if (!this.jeKursDokumentaDostupan) {
      this.iznosUValutiDokumenta = null;
      this.nacinKonverzije = 'rucno';
      this._emitKonverzija();
      return;
    }

    this.iznosUValutiDokumenta = round((this.iznosURsd ?? 0) / this.kursDokumenta, 2);
    if (this.rucniIznos === null) {
      this.rucniIznos = this.iznosUValutiDokumenta;
    }
    if (!this._rucnoOdabrano) {
      this.nacinKonverzije = 'srednji';
    }
    this._emitKonverzija();
  }

  handleChange(event: number) {
    this.iznosChanged.emit(event);
  }

  handleNacinKonverzijeChange() {
    this._rucnoOdabrano = this.nacinKonverzije === 'rucno';
    this._emitKonverzija();
  }

  handleRucniIznosChange(iznos: number) {
    this.rucniIznos = iznos;
    this.nacinKonverzije = 'rucno';
    this._rucnoOdabrano = true;
    this._emitKonverzija();
  }

  private _emitKonverzija() {
    if (this.nacinKonverzije === 'srednji' && this.jeKursDokumentaDostupan) {
      this.konverzijaChanged.emit({
        iznos: this.iznosUValutiDokumenta,
        kurs: this.kursDokumenta,
      });
      return;
    }
    this.konverzijaChanged.emit({
      iznos: this.rucniIznos === null ? null : round(this.rucniIznos, 2),
      kurs: null,
    });
  }
}
